import { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGlobe, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, Tools,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useProcessingFee from '@hooks/useProcessingFee'
import useProcessingFees from '@hooks/useProcessingFees'
import useServices from '@hooks/useServices'
import type { ProcessingFeesFilters } from '@hooks/useProcessingFees'

import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { ServiceModel } from '@models/types'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import ProcessingFeeGroup from '../ProcessingFeeGroup'
import ProcessingFeesBlankState from '../ProcessingFeesBankState'

const buildPageFilterFields = (debtorType: string, services: ServiceModel[]) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Product Title, Reference Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: debtorType !== 'Brand',
      key: 'brand_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      hidden: debtorType !== 'Agency',
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      hidden: debtorType !== 'Provider',
      key: 'provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: services.map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

type DebtorIds = {
  agency_id?: number,
  brand_id?: number,
  provider_id?: number,
}

type ExtendedFilters = ProcessingFeesFilters & DebtorIds

type DebtorType = 'Agency' | 'Brand' | 'Provider'
type QueryParams = { debtor_type?: DebtorType }

const PageContent = (props: { pageFilters: ExtendedFilters }) => {
  const { pageFilters } = props
  const {
    agency_id, brand_id, provider_id, service_id, string,
  } = pageFilters

  useLocation()

  const { debtor_type }: QueryParams = parsePermittedQueryParams(getQueryParamsFromHash(), ['debtor_type'])
  const debtorType: DebtorType = debtor_type || 'Agency'

  const billablePresent = !!service_id || !!string
  const debtorPresent = debtorType === 'Brand' ? !!brand_id : true
  const shouldShowContent = billablePresent && debtorPresent
  const debtorId = pageFilters[`${debtorType?.toLowerCase()}_id` as keyof DebtorIds]

  const initProcessingFee = {
    billable_id: service_id,
    billable_type: 'Service' as const,
    debtor_id: debtorId,
    debtor_type: debtorType,
    global: !agency_id && !provider_id && !brand_id,
  }

  const processingFeesPayload = useProcessingFees({
    filters: {
      ...pageFilters,
      debtor_id: debtorId,
      debtor_type: debtorType,
      global: !agency_id && !provider_id && !brand_id,
      service_id: service_id ? Number(service_id) : undefined,
    },
    performHttpRequests: shouldShowContent,
    requestOptions: defaultRequestOptions.processingFee,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filterAgency,
    filterBrand,
    filteredProcessingFeesCount,
    filterProvider,
    groupedProcessingFees,
    hasFilteredProcessingFees,
    loading,
  } = processingFeesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const {
    callbacks: {
      bulkDeleteForDebtor,
      createOrEditProcessingFee,
    },
    urls: {
      agencyProcessingFeesIndexUrl,
      brandProcessingFeesIndexUrl,
      providerProcessingFeesIndexUrl,
      auditLogProcessingFeesUrl,
    },
  } = useProcessingFee(initProcessingFee)

  const { filteredServices } = useServices()
  const { isMasterAdmin } = useCurrentUser()

  useEffect(() => resetFilters(), [debtorType])

  const filterEntity = filterAgency || filterBrand || filterProvider
  const titleText = !agency_id && !provider_id && !brand_id ? `Global ${debtorType}` : filterEntity?.title

  return (
    <>
      <Helmet>
        <title>Processing Fees | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={isMasterAdmin ? (
          <Button buttonStyle="primaryCreate" onClick={() => createOrEditProcessingFee()} size="medium">
            Create
          </Button>
        ) : null}
        activeTabBarItemKey={debtorType}
        tabBarItems={[
          {
            href: agencyProcessingFeesIndexUrl,
            icon: faGlobe,
            key: 'Agency',
            title: 'Agency',
          },
          {
            href: brandProcessingFeesIndexUrl,
            icon: faGlobe,
            key: 'Brand',
            title: 'Brand',
          },
          {
            href: providerProcessingFeesIndexUrl,
            icon: faGlobe,
            key: 'Provider',
            title: 'Provider',
          },
          {
            href: auditLogProcessingFeesUrl,
            icon: faGlobe,
            key: 'Audit Log',
            title: 'Audit Log',
          },
        ]}
        title={`${debtorType} Processing Fees`}
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && (!hasFilteredProcessingFees || !shouldShowContent) && (
              <ProcessingFeesBlankState
                debtorType={debtor_type}
                hasRequiredFilters={shouldShowContent}
              />
            )}

            {!loading && hasFilteredProcessingFees && shouldShowContent && (
              <DashboardModule
                contentBoxProps={{ flexDirection: 'column' }}
                title={`${titleText} Processing Fees`}
              >
                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection maxHeight={1200}>
                  {hasFilteredProcessingFees && filteredServices.map((service: ServiceModel) => {
                    if (groupedProcessingFees[service.id]){
                      return (
                        <ProcessingFeeGroup
                          key={service.id}
                          initProcessingFee={initProcessingFee}
                          isMasterAdmin={isMasterAdmin}
                          processingFeeGroup={groupedProcessingFees[service.id]}
                          service={service}
                          showAddService={!string}
                        />
                      )
                    }

                    return null
                  })}
                </DashboardModule.ScrollableSection>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredProcessingFeesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              key={debtorType}
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(debtorType, filteredServices)}
              pageFilters={pageFilters}
            />

            {isMasterAdmin && (
              <Tools>
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  // @ts-expect-error TS2322
                  onClick={() => bulkDeleteForDebtor({ debtorId, debtorType })}
                  size="medium"
                >
                  Bulk Delete
                </Button>
              </Tools>
            )}
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
